import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { dataType, Order } from 'components/table/types';
import { DEFAULT_PAGE_SIZE } from 'constants/pagination.constants';
import { useServerPagination } from 'hooks/pagination/useServerPagination';
import {
	mapSystemMessagesSortBy,
	StateScope,
	SYSTEM_MESSAGES_INITIAL_SORT_BY,
	SYSTEM_MESSAGES_INITIAL_SORT_ORDER,
	type StateScope as StateScopeType,
} from 'constants/system-messages.constants';
import {
	clearList,
	getCompanySystemMessages,
	getCompanySystemMessagesTotal,
	getGlobalSystemMessages,
	getGlobalSystemMessagesTotal,
	setCurrentMessage,
} from 'state/reducers/system-messages.reducer';
import {
	fetchCompanySystemMessages,
	fetchSystemMessageById,
	fetchSystemMessages,
} from 'services/system-messages.service';

type UseSystemMessagesTableParams = {
	scope: StateScopeType;
	companyId?: number;
};

export const useSystemMessagesTable = ({ scope, companyId }: UseSystemMessagesTableParams) => {
	const dispatch = useAppDispatch();
	const isCompanyScope = scope === StateScope.Company;
	const rows = useAppSelector(isCompanyScope ? getCompanySystemMessages : getGlobalSystemMessages);
	const total = useAppSelector(isCompanyScope ? getCompanySystemMessagesTotal : getGlobalSystemMessagesTotal);
	const [detailOpen, setDetailOpen] = useState(false);

	const fetchPage = useCallback(
		async (page: number, sortOverride: { sortBy: string; sortOrder: Order }) => {
			const paginationParams = {
				offset: page * DEFAULT_PAGE_SIZE,
				limit: DEFAULT_PAGE_SIZE,
				sortBy: mapSystemMessagesSortBy(sortOverride.sortBy),
				sortOrder: sortOverride.sortOrder,
			};

			if (isCompanyScope) {
				if (!companyId) return;
				await dispatch(fetchCompanySystemMessages({ companyId, ...paginationParams }));
				return;
			}

			await dispatch(fetchSystemMessages(paginationParams));
		},
		[dispatch, isCompanyScope, companyId]
	);

	const { currentPage, isLoading, runFetch, handlePageChange, handleSortChange } = useServerPagination({
		initialSortBy: SYSTEM_MESSAGES_INITIAL_SORT_BY,
		initialSortOrder: SYSTEM_MESSAGES_INITIAL_SORT_ORDER,
		fetchPage,
	});

	useEffect(() => {
		if (isCompanyScope && !companyId) return;
		dispatch(clearList({ scope }));
		runFetch(0);
	}, [dispatch, runFetch, scope, isCompanyScope, companyId]);

	useEffect(() => {
		return () => {
			dispatch(clearList({ scope }));
			dispatch(setCurrentMessage(null));
		};
	}, [dispatch, scope]);

	const handleRow = (row: dataType) => {
		if (isCompanyScope) {
			if (!companyId) return;
			dispatch(fetchSystemMessageById({ id: row.id, companyId }));
		} else {
			dispatch(fetchSystemMessageById({ id: row.id }));
		}
		setDetailOpen(true);
	};

	return {
		rows,
		total,
		currentPage,
		isLoading,
		handlePageChange,
		handleSortChange,
		detailOpen,
		closeDetail: () => setDetailOpen(false),
		handleRow,
		refreshCurrentPage: () => runFetch(currentPage),
	};
};
